// Typed fetch client for the vendor admin API (`/api/v1`, same origin; API_CONTRACTS §1).
// - Session cookie only (`credentials: "same-origin"`); no tokens in JS.
// - Every request carries `X-Ady-Client: 1` (the CSRF rule for non-GET; harmless on GET).
// - Creating POSTs pass an Idempotency-Key; edits of versioned rows pass If-Match ("v<version>").
// - Errors are the shared envelope (packages/contracts) surfaced as ApiError; UI copy comes from the
//   string layer by error code, never from the server message (D-013).
// Request and response shapes mirror the domain handlers (packages/domain/*/src/http.ts and the
// identity handlers-*.ts).
import type { ErrorBody, ErrorCode } from "@ady/contracts";
import type {
  AcceptedInvitation,
  AuditEvent,
  BillingSummary,
  CreatedInvitation,
  CreatedOrg,
  CreatedStore,
  InvitationPreview,
  Entitlements,
  FactorConfirmed,
  Invitation,
  InviteRequest,
  LegalStatus,
  Member,
  MeResponse,
  MfaFactors,
  OtpStartRequest,
  OtpStartResponse,
  OtpVerifyRequest,
  OtpVerifyResponse,
  Page,
  PasskeyOptions,
  PermissionCatalogue,
  Role,
  SessionList,
  StepUpRequest,
  TotpEnrollResponse,
} from "./types";
import { settleIdempotencyKey } from "./idempotency";

export const API_BASE = "/api/v1";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: ErrorCode | "network",
    readonly requestId: string | undefined,
    readonly details: Record<string, unknown> | undefined,
    readonly fields: Record<string, string> | undefined,
    readonly retryAfterS: number | undefined,
  ) {
    super(code);
    this.name = "ApiError";
  }
}

/**
 * Thrown when the user closes "Confirm it's you" instead of confirming: nothing was done. Screens
 * that show `errorMessage(err)` get a plain "nothing was changed"; `stepUpErrorMessage` hides it.
 */
export class StepUpCancelled extends Error {
  constructor() {
    super("stepup_cancelled");
    this.name = "StepUpCancelled";
  }
}

/**
 * Step-up for EVERY call (AUTH-02; owner, 2026-11-05: "Confirm it's you to continue" showed as an error
 * with no popup wherever a screen had not wrapped its call in useStepUp()). A sensitive call answering
 * 401 `auth.stepup_required` opens the dialog through this handler (StepUpProvider installs it), and
 * the same request is sent again once after the user confirms. Safe to repeat: the server refuses
 * before any work, and a refused attempt frees its Idempotency-Key (packages/db/src/idempotency.ts).
 */
type StepUpHandler = (methods: unknown) => Promise<void>;
let stepUpHandler: StepUpHandler | null = null;
export function setStepUpHandler(h: StepUpHandler | null): void {
  stepUpHandler = h;
}

interface RequestOptions {
  /** Internal: this is the one retry after "Confirm it's you" (never asks twice). */
  afterStepUp?: boolean;
  body?: unknown;
  idempotent?: boolean;
  /** Reuse one key across retries of the same action (a lost response must not create twice). */
  idempotencyKey?: string;
  /** The row version the edit was made against; sent as `If-Match: "v<n>"`. */
  ifMatch?: number;
  query?: Record<string, string | number | undefined>;
  signal?: AbortSignal;
  /** A file sent as the body as-is (digital product files), with its own headers. */
  raw?: Blob;
  headers?: Record<string, string>;
}

/**
 * DEV mock API only (mocks/browser.ts): an API call answered by Vite (its index.html, or an empty 404
 * for a POST/PATCH) means the browser restarted the mock worker and it forgot this page; the mocks
 * themselves always answer errors in JSON. Re-activates it and returns true so the caller retries
 * once. In production the hook is never installed, so this is always false.
 */
export async function reviveMocks(res: {
  status?: number;
  headers: { get(name: string): string | null };
}): Promise<boolean> {
  const revive = (globalThis as { __ADY_MOCK_REVIVE__?: () => Promise<void> }).__ADY_MOCK_REVIVE__;
  const type = res.headers.get("content-type") ?? "";
  const fromVite = type.startsWith("text/html") || (res.status === 404 && !type.includes("json"));
  if (!revive || !fromVite) return false;
  await revive();
  return true;
}

export async function request<T>(
  method: string,
  path: string,
  options: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { "X-Ady-Client": "1", Accept: "application/json" };
  if (options.body !== undefined) headers["Content-Type"] = "application/json";
  if (options.idempotencyKey) headers["Idempotency-Key"] = options.idempotencyKey;
  else if (options.idempotent) headers["Idempotency-Key"] = crypto.randomUUID();
  if (options.ifMatch !== undefined) headers["If-Match"] = `"v${options.ifMatch}"`;
  Object.assign(headers, options.headers);
  const query = new URLSearchParams();
  for (const [k, v] of Object.entries(options.query ?? {}))
    if (v !== undefined) query.set(k, String(v));
  const qs = query.toString();
  let res: Response;
  const send = () =>
    fetch(`${API_BASE}${path}${qs ? `?${qs}` : ""}`, {
      method,
      headers,
      credentials: "same-origin",
      body: options.raw ?? (options.body === undefined ? undefined : JSON.stringify(options.body)),
      signal: options.signal,
    });
  try {
    res = await send();
    if (await reviveMocks(res)) res = await send();
  } catch {
    throw new ApiError(0, "network", undefined, undefined, undefined, undefined);
  }
  // A 2xx ends this intent: the key stores forget the key, so the next identical action is new.
  if (res.ok && options.idempotencyKey) settleIdempotencyKey(options.idempotencyKey);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  let json: unknown;
  try {
    json = text ? JSON.parse(text) : undefined;
  } catch {
    json = undefined;
  }
  if (!res.ok) {
    const err = (json as ErrorBody | undefined)?.error;
    const retry = res.headers.get("Retry-After");
    if (
      res.status === 401 &&
      err?.code === "auth.stepup_required" &&
      stepUpHandler &&
      !options.afterStepUp &&
      path !== "/auth/stepup"
    ) {
      await stepUpHandler(err.details?.methods); // StepUpCancelled when the user closes it
      return request<T>(method, path, { ...options, afterStepUp: true });
    }
    throw new ApiError(
      res.status,
      err?.code ?? "internal",
      err?.request_id,
      err?.details,
      err?.fields,
      retry ? Number(retry) : undefined,
    );
  }
  return json as T;
}

const org = (orgId: string) => `/orgs/${encodeURIComponent(orgId)}`;

export const api = {
  // ---------------------------------------------------------------- AUTH-001 (API_CONTRACTS §3.1)
  me: (signal?: AbortSignal) => request<MeResponse>("GET", "/me", { signal }),
  otpStart: (body: OtpStartRequest) =>
    request<OtpStartResponse>("POST", "/auth/otp/start", { body }),
  otpVerify: (body: OtpVerifyRequest) =>
    request<OtpVerifyResponse>("POST", "/auth/otp/verify", { body }),
  stepUp: (body: StepUpRequest) => request<{ mfa_at: string }>("POST", "/auth/stepup", { body }),
  /** D-126: the account's own mobile number (adding or removing one needs a step-up). */
  phoneStart: (phone: string, orgId: string) =>
    request<{ challenge_id: string; resend_after_s: number }>("POST", "/me/phone/start", {
      body: { phone, org_id: orgId },
    }),
  phoneVerify: (challengeId: string, code: string) =>
    request<{ phone: string }>("POST", "/me/phone/verify", {
      body: { challenge_id: challengeId, code },
    }),
  phoneRemove: () => request<void>("DELETE", "/me/phone"),
  mfaFactors: () => request<MfaFactors>("GET", "/auth/mfa"),
  passkeyOptions: (purpose: "register" | "signin" | "stepup") =>
    request<PasskeyOptions>("POST", "/auth/mfa/passkeys/options", { body: { purpose } }),
  passkeyRegister: (challengeId: string, response: unknown) =>
    request<FactorConfirmed & { registered: true }>("POST", "/auth/mfa/passkeys/register", {
      body: { challenge_id: challengeId, response },
    }),
  passkeyAssert: (challengeId: string, response: unknown) =>
    request<OtpVerifyResponse>("POST", "/auth/mfa/passkeys/assert", {
      body: { challenge_id: challengeId, response },
    }),
  totpEnroll: () => request<TotpEnrollResponse>("POST", "/auth/mfa/totp/enroll", { body: {} }),
  totpConfirm: (code: string) =>
    request<FactorConfirmed & { confirmed: true }>("POST", "/auth/mfa/totp/confirm", {
      body: { code },
    }),
  sessions: () => request<SessionList>("GET", "/auth/sessions"),
  revokeSession: (id: string) =>
    request<void>("DELETE", `/auth/sessions/${encodeURIComponent(id)}`),
  revokeOtherSessions: () => request<{ revoked: number }>("DELETE", "/auth/sessions"),
  logout: () => request<void>("POST", "/auth/logout", { body: {} }),

  // ---------------------------------------------------------------- ORG-001 (§3.2)
  /** Business setup. Both creates are idempotent. */
  createOrg: (name: string, idempotencyKey: string, referralCode?: string) =>
    request<CreatedOrg>("POST", "/orgs", {
      // PARTNER-01 (P7-A): optional; the server normalises and validates it.
      body: referralCode ? { name, referral_code: referralCode } : { name },
      idempotencyKey,
    }),
  createStore: (orgId: string, body: { name: string; slug: string }, idempotencyKey: string) =>
    request<CreatedStore>("POST", `${org(orgId)}/stores`, { body, idempotencyKey }),
  /** ORG-03 versioned platform terms. */
  legal: () => request<LegalStatus>("GET", "/legal/current"),
  acceptLegal: (documentIds: string[]) =>
    request<LegalStatus>("POST", "/legal/accept", { body: { document_ids: documentIds } }),

  // ---------------------------------------------------------------- TEAM-001 (§3.3)
  members: (orgId: string) => request<Page<Member>>("GET", `${org(orgId)}/members`),
  /**
   * Sensitive (roles.manage, S): may answer 401 auth.stepup_required. `version` is sent as
   * If-Match when the member row carries one (the Phase 4 member shape has no version yet).
   */
  changeMemberRole: (orgId: string, memberId: string, roleId: string, version?: number) =>
    request<Member>("PATCH", `${org(orgId)}/members/${encodeURIComponent(memberId)}`, {
      body: { role_id: roleId },
      ifMatch: version,
    }),
  removeMember: (orgId: string, memberId: string) =>
    request<void>("DELETE", `${org(orgId)}/members/${encodeURIComponent(memberId)}`),
  roles: (orgId: string) => request<Page<Role>>("GET", `${org(orgId)}/roles`),
  permissions: () => request<PermissionCatalogue>("GET", "/permissions"),
  createRole: (orgId: string, body: { name: string; permissions: string[] }) =>
    request<Role>("POST", `${org(orgId)}/roles`, { body }),
  /** A custom role's name and permissions; `version` guards against a concurrent edit. */
  updateRole: (
    orgId: string,
    roleId: string,
    body: { name?: string; permissions?: string[] },
    version: number,
  ) =>
    request<Role>("PATCH", `${org(orgId)}/roles/${encodeURIComponent(roleId)}`, {
      body,
      ifMatch: version,
    }),
  invitations: (orgId: string) => request<Page<Invitation>>("GET", `${org(orgId)}/invitations`),
  invite: (orgId: string, body: InviteRequest, idempotencyKey: string) =>
    request<CreatedInvitation>("POST", `${org(orgId)}/invitations`, { body, idempotencyKey }),
  revokeInvitation: (orgId: string, invitationId: string) =>
    request<void>("DELETE", `${org(orgId)}/invitations/${encodeURIComponent(invitationId)}`),
  invitationPreview: (token: string) =>
    request<InvitationPreview>("GET", `/invitations/${encodeURIComponent(token)}`),
  acceptInvitation: (token: string) =>
    request<AcceptedInvitation>("POST", `/invitations/${encodeURIComponent(token)}/accept`, {
      body: {},
    }),

  // ---------------------------------------------------------------- AUDIT-001 (§3.4)
  audit: (orgId: string, params: { cursor?: string; limit?: number; action?: string } = {}) =>
    request<Page<AuditEvent>>("GET", `${org(orgId)}/audit`, { query: params }),

  // ---------------------------------------------------------------- BILLING-001/09 (§3.6)
  entitlements: (orgId: string) => request<Entitlements>("GET", `${org(orgId)}/entitlements`),
  billing: (orgId: string) => request<BillingSummary>("GET", `${org(orgId)}/billing`),

  /** Google OIDC is a top-level navigation, not a fetch. */
  googleStartUrl: `${API_BASE}/auth/google/start`,
};
